import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, Upload, Check, X, CalendarDays, Lock, Trash2, History, ExternalLink } from "lucide-react";
import { Spinner } from "../components/ui/kit";
import PodpisSheet from "../components/PodpisSheet";
import FotoLightbox from "../components/FotoLightbox";
import { addMinutes, fmtHodiny, fmtYmd, ZAVAZNOST_LABEL } from "../lib/servis";
import NahratFotky from "../components/NahratFotky";
import { apiError, souborUrl } from "../lib/api";
import api from "../lib/api";
import { Zpet, useNazevStranky } from "../lib/navigace";
import { pripravOkno, otevritPdf, zavritOkno } from "../lib/pdf";
import PdfPripraveno from "../components/PdfPripraveno";
import Vyber from "../components/Vyber";

const DOW = ["neděle", "pondělí", "úterý", "středa", "čtvrtek", "pátek", "sobota"];
function dowLabel(ymd) {
  const d = new Date(ymd + "T00:00:00");
  return isNaN(d) ? "" : DOW[d.getDay()];
}
function today() { return new Date().toISOString().slice(0, 10); }

export default function ServisZapis() {
  const { id } = useParams();
  const nav = useNavigate();
  const saveTimer = useRef(null);

  const [z, setZ] = useState(undefined);
  const [zapis, setZapis] = useState("");
  const [bezZavad, setBezZavad] = useState(false);
  const [body, setBody] = useState([]);
  const [jmeno, setJmeno] = useState("");
  const [saveState, setSaveState] = useState("");         // "saving" | "saved" | ""
  const [nErr, setNErr] = useState("");
  const [pdfState, setPdfState] = useState("");
  const [pdfOdkaz, setPdfOdkaz] = useState(null);
  const [lightbox, setLightbox] = useState(null);      // index otevřené fotky
  const [podpisSheet, setPodpisSheet] = useState(null);   // "KLIENT" | "TECHNIK" | null
  const cekajici = useRef(null);                            // patch, který ještě neodešel
  const [nahrat, setNahrat] = useState(false);
  const [smazat, setSmazat] = useState(false);
  const [novy, setNovy] = useState({ popis: "", zavaznost: "STREDNI", termin: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/zapisy/${id}`);
      setZ(data);
      setZapis(data.zapis || "");
      setBezZavad(!!data.bez_zavad);
      setBody(data.body || []);
      setJmeno(data.podpis_klient_jmeno || "");
    } catch { setZ(null); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useNazevStranky(z && `Zápis ${z.cislo || ""}`.trim());

  // reload jen časů/nedostatků/fotek bez přepsání rozpracovaných polí
  const refresh = useCallback(async () => {
    const { data } = await api.get(`/api/zapisy/${id}`);
    setZ((prev) => ({ ...data, }));
  }, [id]);

  // Změny se slévají do jednoho patche a odcházejí s odstupem; „Uložit"
  // v patičce je pošle hned, aby odchod ze stránky nic nezahodil.
  const odesliCekajici = useCallback(async () => {
    clearTimeout(saveTimer.current);
    const patch = cekajici.current;
    if (!patch) return;
    cekajici.current = null;
    try {
      await api.patch(`/api/zapisy/${id}`, patch);
      setSaveState("saved");
      setTimeout(() => setSaveState(""), 1500);
    } catch { setSaveState(""); }
  }, [id]);
  const scheduleSave = useCallback((patch) => {
    setSaveState("saving");
    cekajici.current = { ...(cekajici.current || {}), ...patch };
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(odesliCekajici, 800);
  }, [odesliCekajici]);
  const ulozitAZpet = async () => {
    await odesliCekajici();
    nav(`/servis/zarizeni/${z.zarizeni_id}`);
  };

  const onZapis = (v) => { setZapis(v); scheduleSave({ zapis: v }); };
  const onBezZavad = () => { const nx = !bezZavad; setBezZavad(nx); scheduleSave({ bez_zavad: nx }); };
  const onJmeno = (v) => { setJmeno(v); scheduleSave({ podpis_klient_jmeno: v }); };
  const setBod = (bid, val) => {
    const nb = body.map((b) => b.id === bid ? { ...b, ...val } : b);
    setBody(nb);
    scheduleSave({ body: nb.map((b) => ({ id: b.id, ok: b.ok, poznamka: b.poznamka })) });
  };

  // ---- časy ----
  const addDay = async () => {
    // Druhý den navazuje na poslední zapsaný, ne na dnešek — zápis se často
    // dopisuje zpětně. Datum jde stejně u každého dne přepsat.
    const posledni = ((z && z.casy) || []).map((c) => c.datum).filter(Boolean).sort().pop();
    let datum = today();
    if (posledni) {
      const d = new Date(posledni + "T00:00:00");
      d.setDate(d.getDate() + 1);
      const dalsi = d.toISOString().slice(0, 10);
      datum = dalsi > today() ? today() : dalsi;
    }
    await api.post(`/api/zapisy/${id}/casy`, { datum, prichod: "08:00", odchod: "16:00", hodiny: 8 });
    refresh();
  };
  const patchDay = async (c, patch) => {
    const merged = { datum: c.datum, prichod: c.prichod, odchod: c.odchod, hodiny: c.hodiny, ...patch };
    // odjezd dopočítáme z příjezdu + hodin
    merged.odchod = addMinutes(merged.prichod, Math.round((merged.hodiny || 0) * 60));
    await api.patch(`/api/zapisy/${id}/casy/${c.id}`, merged);
    refresh();
  };
  const delDay = async (cid) => { await api.delete(`/api/zapisy/${id}/casy/${cid}`); refresh(); };

  // ---- fotky ----
  const onPhotos = async (files) => {
    if (!files.length) return;
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    await api.post(`/api/zapisy/${id}/fotky`, fd, { headers: { "Content-Type": undefined } });
    refresh();
  };
  const delPhoto = async (fid) => { await api.delete(`/api/zapisy/${id}/fotky/${fid}`); refresh(); };

  // ---- nedostatky ----
  const addNedostatek = async () => {
    if (!novy.popis.trim()) return;
    await api.post("/api/nedostatky", {
      zarizeni_id: z.zarizeni_id, zapis_id: id, popis: novy.popis,
      zavaznost: novy.zavaznost, termin: novy.termin || null,
    });
    setNovy({ popis: "", zavaznost: "STREDNI", termin: "" });
    refresh();
  };

  // ---- podpis ----
  const savePodpis = async (dataUrl) => {
    await api.post(`/api/zapisy/${id}/podpis`, { kdo: podpisSheet || "KLIENT", data_url: dataUrl, jmeno });
    await refresh();
  };

  const smazZapis = async () => {
    try {
      await api.delete(`/api/zapisy/${id}`);
      nav(`/servis/zarizeni/${z.zarizeni_id}`);
    } catch (e) {
      setSmazat(false);
      setNErr(apiError(e.response?.data?.detail) || "Zápis se nepodařilo smazat.");
    }
  };

  const dokoncit = async () => {
    setNErr("");
    try {
      await api.post(`/api/zapisy/${id}/dokoncit`);
      nav(`/servis/zarizeni/${z.zarizeni_id}`);
    } catch (e) {
      setNErr(apiError(e.response?.data?.detail) || "Nepodařilo se dokončit.");
    }
  };

  const stahnoutPdf = async () => {
    // Záložka se otevře hned při kliknutí — později by ji prohlížeč zablokoval (lib/pdf.js).
    const okno = pripravOkno();
    setNErr(""); setPdfState("loading"); setPdfOdkaz(null);
    try {
      const { data } = await api.post(`/api/zapisy/${id}/pdf`);
      const url = souborUrl(data.url);
      if (!otevritPdf(okno, url)) setPdfOdkaz(url);
    } catch (e) {
      zavritOkno(okno);
      setNErr(apiError(e.response?.data?.detail) || "PDF se nepodařilo vygenerovat.");
    } finally { setPdfState(""); }
  };

  const odeslat = async () => {
    setNErr("");
    try {
      const { data } = await api.post(`/api/zapisy/${id}/odeslat`, {});
      setNErr(`Zápis odeslán na ${data.email}.`);
      refresh();
    } catch (e) {
      setNErr(apiError(e.response?.data?.detail) || "Zápis se nepodařilo odeslat.");
    }
  };

  if (z === undefined) return <div className="p-10 flex justify-center text-muted"><Spinner /></div>;
  if (z === null) return <div className="p-10 text-center text-muted">Zápis nenalezen.</div>;

  const casy = z.casy || [];
  const nedostatky = z.nedostatky || [];
  const fotky = z.fotky_urls || [];
  const boduOk = body.filter((b) => b.ok !== null && b.ok !== undefined).length;
  // Zámek i oprávnění říká server (`muzu_editovat`), UI si nic nedopočítává.
  const muzuEditovat = z.muzu_editovat !== false;
  const uzamceno = !!z.uzamceno;

  return (
    <div className="max-w-[560px] mx-auto pb-28" data-testid="servis-zapis-page">
      <Zpet className="mb-3" nahradni={`/servis/zarizeni/${z.zarizeni_id}`} nahradniNazev="Zařízení" />
      <div className="flex items-center gap-3 mb-4">
        <div className="min-w-0">
          <div className="text-[13px] text-sec truncate">{z.zarizeni?.nazev} · {fmtHodiny(z.hodiny_celkem)}</div>
          {/* Datum zápisu = první odpracovaný den; mění se přepsáním data u dne níž. */}
          <div className="text-[12px] text-muted truncate" data-testid="zapis-datum">
            {z.cislo} · {fmtYmd(z.datum)}
          </div>
        </div>
        <span className="ml-auto label-up px-2 py-1 rounded-pill bg-[#fdf1e8] text-[#c8551a] shrink-0">
          {z.stav === "ROZPRACOVANY" ? "Rozpracováno" : z.stav === "PODEPSANY" ? "Podepsáno" : z.stav === "ODESLANY" ? "Odesláno" : "Dokončeno"}
        </span>
        {saveState && (
          <span className="text-[11px] text-muted shrink-0" data-testid="save-state">
            {saveState === "saving" ? "Ukládám…" : "Uloženo"}
          </span>
        )}
      </div>

      {uzamceno && (
        <div className="card px-4 py-3 mb-3 flex items-center gap-2.5" data-testid="zapis-uzamceno">
          <Lock size={15} className="text-muted shrink-0" />
          <span className="text-[12.5px] text-sec">
            {muzuEditovat
              ? "Zápis je podepsaný. Změny dělej jen po dohodě s klientem — podpis se tím ruší."
              : "Zápis je podepsaný a uzamčený. Opravu může udělat majitel nebo asistentka."}
          </span>
        </div>
      )}

      {z.import_zdroj && (
        <div className="card px-4 py-3 mb-3 flex items-center gap-2.5" data-testid="zapis-import">
          <History size={15} className="text-muted shrink-0" />
          <span className="t-meta text-sec min-w-0">
            Převzato z {z.import_zdroj}{z.puvodne_podepsano ? " · klient podepsal v původním systému" : ""}
          </span>
          {z.puvodni_pdf_url && (
            <a href={z.puvodni_pdf_url} target="_blank" rel="noreferrer" data-testid="zapis-import-pdf"
               className="tap ml-auto shrink-0 inline-flex items-center gap-1 t-meta font-600 text-accent">
              Původní protokol <ExternalLink size={13} />
            </a>
          )}
        </div>
      )}

      {/* Odpracované dny */}
      <div className="card p-4 mb-3">
        <div className="flex items-baseline gap-2">
          <div className="label-up text-muted">Odpracované dny</div>
          <div className="ml-auto amount text-[12px] font-700 text-accent">{fmtHodiny(z.hodiny_celkem)}</div>
        </div>
        {casy.map((c) => {
          const odjezd = addMinutes(c.prichod || "08:00", Math.round((c.hodiny || 0) * 60));
          return (
            <div key={c.id} className="mt-3 rounded-card border border-line-strong p-3 bg-white" data-testid={`den-${c.id}`}>
              {/* Datum musí jít přepsat — zápis se často píše až po předání
                  nebo se doplňuje včerejšek. */}
              <div className="flex items-center gap-2">
                <CalendarDays size={15} className="text-accent shrink-0" />
                <input type="date" value={c.datum || ""} max={today()} disabled={!muzuEditovat}
                  onChange={(e) => e.target.value && patchDay(c, { datum: e.target.value })}
                  data-testid={`den-datum-${c.id}`}
                  className="amount text-[13.5px] font-700 text-ink bg-transparent border border-transparent hover:border-line-strong focus:border-accent rounded-input px-1.5 h-8 outline-none cursor-pointer" />
                <span className="text-[11px] text-muted">{dowLabel(c.datum)}</span>
                {muzuEditovat && (
                  <button onClick={() => delDay(c.id)} data-testid={`den-del-${c.id}`} className="ml-auto text-muted hover:text-danger"><X size={16} /></button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <Stepper label="Příjezd" value={c.prichod || "—"} disabled={!muzuEditovat}
                  onMinus={() => patchDay(c, { prichod: addMinutes(c.prichod || "08:00", -30) })}
                  onPlus={() => patchDay(c, { prichod: addMinutes(c.prichod || "08:00", 30) })}
                  testid={`prichod-${c.id}`} />
                <Stepper label="Odpracováno" value={fmtHodiny(c.hodiny)} disabled={!muzuEditovat}
                  onMinus={() => patchDay(c, { hodiny: Math.max(0, (c.hodiny || 0) - 0.5) })}
                  onPlus={() => patchDay(c, { hodiny: (c.hodiny || 0) + 0.5 })}
                  testid={`hodiny-${c.id}`} />
              </div>
              <div className="mt-3 flex items-center gap-2 bg-ink rounded-icon px-3 py-2">
                <span className="label-up text-white/50">Odjezd</span>
                <span className="ml-auto amount text-[14px] font-700 text-white">{odjezd}</span>
              </div>
            </div>
          );
        })}
        {muzuEditovat && <button onClick={addDay} data-testid="pridat-den"
          className="mt-3 w-full rounded-card border border-dashed border-accent/50 py-3 text-[13px] font-700 text-accent hover:bg-accent/5">
          + Přidat další den
        </button>}
      </div>

      {/* Zápis o práci */}
      <div className="card p-4 mb-3">
        <div className="label-up text-muted mb-2">Zápis o práci</div>
        <textarea value={zapis} onChange={(e) => onZapis(e.target.value)} disabled={!muzuEditovat} data-testid="zapis-text"
          placeholder="Co jsme na místě udělali…"
          className="field min-h-[104px] resize-y text-[13px] leading-relaxed" />
      </div>

      {/* Kontrolní body */}
      {body.length > 0 && (
        <div className="card p-4 mb-3">
          <div className="flex items-center gap-2 mb-3">
            <div className="label-up text-muted">Kontrolní body</div>
            <span className="ml-auto amount text-[12px] font-700 text-sec">{boduOk} / {body.length} bodů</span>
          </div>
          <div className="space-y-2">
            {body.map((b) => (
              <div key={b.id} className="flex items-center gap-2" data-testid={`bod-${b.id}`}>
                <span className="flex-1 text-[13px] text-ink">{b.text}</span>
                <button onClick={() => setBod(b.id, { ok: true })} disabled={!muzuEditovat} data-testid={`bod-ok-${b.id}`}
                  className={`h-8 w-8 rounded-icon flex items-center justify-center border ${b.ok === true ? "bg-success-bg border-success text-success" : "border-line-strong text-muted"}`}><Check size={16} /></button>
                <button onClick={() => setBod(b.id, { ok: false })} disabled={!muzuEditovat} data-testid={`bod-nok-${b.id}`}
                  className={`h-8 w-8 rounded-icon flex items-center justify-center border ${b.ok === false ? "bg-danger/10 border-danger text-danger" : "border-line-strong text-muted"}`}><X size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Nedostatky */}
      <div className="card p-4 mb-3">
        <div className="flex items-center gap-2">
          <div className="label-up text-muted">Nedostatky</div>
          <button onClick={onBezZavad} disabled={!muzuEditovat} data-testid="bez-zavad-toggle" className="ml-auto flex items-center gap-2 disabled:opacity-50">
            <span className={`h-5 w-5 rounded-[6px] border flex items-center justify-center ${bezZavad ? "bg-success border-success text-white" : "border-line-strong"}`}>
              {bezZavad && <Check size={13} />}
            </span>
            <span className="text-[12px] font-600 text-sec">Bez závad</span>
          </button>
        </div>
        {bezZavad ? (
          <div className="mt-3 rounded-card bg-success-bg border border-success/30 px-3 py-2.5 text-[12.5px] font-600 text-success">Zařízení předáno bez závad</div>
        ) : (
          <div className="mt-3 space-y-2">
            {nedostatky.map((n) => (
              <div key={n.id} className="rounded-card bg-danger/5 border border-danger/20 px-3 py-2.5">
                <div className="text-[13px] font-600 text-[#8f2419]">{n.popis}</div>
                <div className="text-[11px] text-danger/80 mt-0.5">{ZAVAZNOST_LABEL[n.zavaznost] || n.zavaznost}{n.termin ? ` · do ${n.termin}` : ""}</div>
              </div>
            ))}
            {muzuEditovat && <div className="rounded-card border border-line-strong p-3 space-y-2">
              <input value={novy.popis} onChange={(e) => setNovy({ ...novy, popis: e.target.value })} data-testid="nedostatek-popis"
                placeholder="Co je vadné, co doporučuješ…" className="field text-[13px]" />
              <div className="flex gap-2">
                <Vyber value={novy.zavaznost} onChange={(e) => setNovy({ ...novy, zavaznost: e.target.value })} className="field text-[13px] flex-1" data-testid="nedostatek-zavaznost">
                  {Object.entries(ZAVAZNOST_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </Vyber>
                <input type="date" value={novy.termin} onChange={(e) => setNovy({ ...novy, termin: e.target.value })} className="field text-[13px] flex-1" />
                <button onClick={addNedostatek} data-testid="nedostatek-add" className="btn-primary h-9 px-3"><Plus size={15} /></button>
              </div>
            </div>}
          </div>
        )}
      </div>

      {/* Fotky */}
      <div className="card p-4 mb-3">
        <div className="label-up text-muted mb-3">Fotky</div>
        <div className="grid grid-cols-3 gap-2">
          {fotky.map((f, i) => (
            <div key={f.file_id} className="relative aspect-square rounded-icon overflow-hidden border border-line-strong">
              {/* klik zvětší — z náhledu technik nepozná, co vlastně vyfotil */}
              <button onClick={() => setLightbox(i)} data-testid={`foto-open-${f.file_id}`} className="block w-full h-full">
                <img src={souborUrl(f.url)} alt="" className="w-full h-full object-cover" />
              </button>
              {muzuEditovat && (
                <button onClick={() => delPhoto(f.file_id)} data-testid={`foto-del-${f.file_id}`}
                  className="absolute top-1 right-1 h-7 w-7 rounded-full bg-black/60 text-white flex items-center justify-center"><X size={14} /></button>
              )}
            </div>
          ))}
          {/* Jedno tlačítko; zdroj (fotoaparát / galerie) se vybere až v overlayi. */}
          {muzuEditovat && (
            <button onClick={() => setNahrat(true)} data-testid="foto-add"
              className="aspect-square rounded-icon border border-dashed border-line-strong flex flex-col items-center justify-center gap-1 text-muted hover:bg-inputbg">
              <Upload size={20} className="text-accent" /><span className="text-[10px] font-600">Nahrát</span>
            </button>
          )}
        </div>
      </div>

      {/* Podpisy: technik (zhotovitel) i klient — oba jdou do PDF. */}
      <div className="card p-4 mb-3 grid md:grid-cols-2 gap-4">
        <PodpisBlok titul="Podpis technika" url={z.podpis_technik_url} popisek="Podepsáno"
          muzeEditovat={muzuEditovat} onOtevrit={() => setPodpisSheet("TECHNIK")} testid="podpis-technik" />
        <PodpisBlok titul="Podpis klienta" url={z.podpis_klient_url} popisek={z.podpis_klient_jmeno || "Podepsáno"}
          muzeEditovat={muzuEditovat} onOtevrit={() => setPodpisSheet("KLIENT")} testid="podpis" />
      </div>

      {nErr && <div className="card px-4 py-3 mb-3 text-[13px] text-danger" data-testid="dokoncit-error">{nErr}</div>}

      {/* patička */}
      {/* Patička: na telefonu palcová (tlačítko přes šířku), na desktopu
          zarovnaná k obsahu a v běžné velikosti — přes celou obrazovku
          působilo jako chyba. */}
      {/* Na telefonu a tabletu plave nad dolní lištou (16A), na počítači je u spodní hrany. */}
      <div className="fixed z-30 left-3 right-3 md:left-[92px] md:right-6 bottom-[var(--nad-listou)] rounded-[22px] shadow-panel bg-surface/95 backdrop-blur border border-line-strong lg:bottom-0 lg:left-[236px] lg:right-0 lg:rounded-none lg:shadow-none lg:border-0 lg:border-t">
        <div className="max-w-[560px] mx-auto px-4 py-2.5 flex gap-2 items-center">
          <button onClick={ulozitAZpet} data-testid="zapis-ulozit"
            className="btn-ghost h-10 px-4">{z.stav === "ROZPRACOVANY" ? "Uložit" : "Uložit a zpět"}</button>
          {z.muzu_smazat && (
            <button onClick={() => setSmazat(true)} data-testid="zapis-smazat" title="Smazat zápis"
              className="btn-ghost h-10 w-10 px-0 justify-center text-muted hover:text-danger"><Trash2 size={16} /></button>
          )}
          {z.stav === "ROZPRACOVANY" ? (
            <button onClick={dokoncit} data-testid="zapis-dokoncit"
              className="btn-primary flex-1 md:flex-none md:ml-auto h-10 px-6 justify-center">Dokončit</button>
          ) : (
            <>
              <button onClick={stahnoutPdf} data-testid="zapis-pdf"
                className="btn-ghost h-10 px-4">{pdfState === "loading" ? <Spinner /> : "PDF"}</button>
              <button onClick={odeslat} data-testid="zapis-odeslat"
                className="btn-primary flex-1 md:flex-none md:ml-auto h-10 px-5 justify-center">
                {z.stav === "ODESLANY" ? "Odeslat znovu" : "Odeslat klientovi"}
              </button>
            </>
          )}
        </div>
      </div>

      {lightbox !== null && (
        <FotoLightbox fotky={fotky} index={lightbox} onIndex={setLightbox} onClose={() => setLightbox(null)} />
      )}
      {podpisSheet === "KLIENT" && (
        <PodpisSheet jmeno={jmeno} onJmeno={onJmeno} onSave={savePodpis} onClose={() => setPodpisSheet(null)} />
      )}
      {podpisSheet === "TECHNIK" && (
        <PodpisSheet titulek="Podpis technika" onSave={savePodpis} onClose={() => setPodpisSheet(null)} />
      )}
      {nahrat && <NahratFotky onFiles={onPhotos} onClose={() => setNahrat(false)} />}
      {smazat && (
        <div className="fixed inset-0 z-[70] flex items-end md:items-center justify-center md:p-4" data-testid="smazat-dialog">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSmazat(false)} />
          <div className="relative bg-white w-full md:max-w-[400px] rounded-t-sheet md:rounded-card shadow-panel p-5">
            <h2 className="text-[15px] font-800 text-ink">Smazat zápis?</h2>
            <p className="text-[13px] text-sec mt-1.5">
              Zápis {z.cislo} se odstraní i s časy a fotkami. Vrátit to nejde.
            </p>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setSmazat(false)} className="btn-ghost h-10 px-4 flex-1 justify-center">Zrušit</button>
              <button onClick={smazZapis} data-testid="smazat-potvrdit"
                className="pill h-10 px-4 flex-1 justify-center inline-flex items-center gap-2 font-semibold bg-danger text-white hover:opacity-90">
                Smazat
              </button>
            </div>
          </div>
        </div>
      )}
      <PdfPripraveno url={pdfOdkaz} nazev="PDF zápisu" onClose={() => setPdfOdkaz(null)} />
    </div>
  );
}

function PodpisBlok({ titul, url, popisek, muzeEditovat, onOtevrit, testid }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between mb-2">
        <div className="label-up text-muted">{titul}</div>
        {url && muzeEditovat && (
          <button onClick={onOtevrit} className="text-[12px] font-600 text-accent"
            data-testid={`${testid}-znovu`}>Podepsat znovu</button>
        )}
      </div>
      {url ? (
        <>
          <div className="rounded-card border border-line-strong p-2 bg-white">
            <img src={souborUrl(url)} alt="podpis" className="h-24 mx-auto object-contain" />
          </div>
          <div className="text-[12px] text-muted mt-1.5 text-center">{popisek}</div>
        </>
      ) : (
        <button onClick={onOtevrit} disabled={!muzeEditovat} data-testid={`${testid}-otevrit`}
          className="tap w-full rounded-card border border-dashed border-line-strong bg-[#fbfbf9] h-24 text-[13px] text-muted hover:bg-inputbg disabled:opacity-50">
          Podepiš prstem sem
        </button>
      )}
    </div>
  );
}

function Stepper({ label, value, onMinus, onPlus, testid, disabled }) {
  return (
    <div className="min-w-0">
      <div className="label-up text-muted mb-1.5">{label}</div>
      <div className="flex items-center gap-1.5">
        <button onClick={onMinus} disabled={disabled} data-testid={`${testid}-minus`} className="h-9 w-9 rounded-icon border border-line-strong text-sec font-700 text-[17px] flex items-center justify-center shrink-0 disabled:opacity-40">−</button>
        <span className="flex-1 text-center amount text-[14px] font-700 text-ink">{value}</span>
        <button onClick={onPlus} disabled={disabled} data-testid={`${testid}-plus`} className="h-9 w-9 rounded-icon border border-line-strong text-sec font-700 text-[17px] flex items-center justify-center shrink-0 disabled:opacity-40">+</button>
      </div>
    </div>
  );
}
